import { CloudApiError, createCloudApiClient } from './api-client';
import type { CloudApiClient, CloudPrincipal } from './api-client';
import type { CloudConfig } from './config';
import {
  createAuthorizationUrl,
  createLogoutUrl,
  discoverCognito,
  exchangeAuthorizationCallback,
  refreshCognitoTokens,
} from './oauth';
import type { CognitoDiscovery } from './oauth';
import {
  clearCloudTokens,
  isAccessTokenUsable,
  loadCloudTokens,
  saveCloudTokens,
} from './token-store';

const PRINCIPAL_STORAGE_KEY = 'budget.cloud.principal.v1';

type StorageLike = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>;

export type CloudSessionSnapshot =
  | Readonly<{ status: 'signed-out' }>
  | Readonly<{
      status: 'authenticated';
      isOffline: boolean;
      principal: CloudPrincipal;
    }>;

export type CloudSessionManager = Readonly<{
  api: CloudApiClient;
  beginLogin: (returnTo?: string) => Promise<string>;
  completeLogout: () => Promise<string | null>;
  getAccessToken: () => Promise<string>;
  restore: (currentUrl?: string) => Promise<CloudSessionSnapshot>;
}>;

type SessionDependencies = Readonly<{
  fetcher?: typeof fetch;
  now?: () => number;
  persistentStorage?: StorageLike;
  transactionStorage?: StorageLike;
}>;

export class CloudSessionError extends Error {
  constructor(
    public readonly code: 'expired' | 'missing-session',
    message: string,
  ) {
    super(message);
    this.name = 'CloudSessionError';
  }
}

export function createCloudSessionManager(
  config: CloudConfig,
  dependencies: SessionDependencies = {},
): CloudSessionManager {
  const fetcher = dependencies.fetcher ?? fetch;
  const now = dependencies.now ?? Date.now;
  const persistentStorage = dependencies.persistentStorage ?? localStorage;
  const transactionStorage = dependencies.transactionStorage ?? sessionStorage;
  let discovery: CognitoDiscovery | null = null;

  async function getDiscovery(): Promise<CognitoDiscovery> {
    discovery ??= await discoverCognito(config, fetcher);
    return discovery;
  }

  async function getAccessToken(): Promise<string> {
    const tokens = loadCloudTokens(persistentStorage);
    if (tokens === null) {
      throw new CloudSessionError(
        'missing-session',
        'Cloud login is required.',
      );
    }
    if (isAccessTokenUsable(tokens, now())) return tokens.accessToken;
    if (tokens.refreshToken === null) {
      clearSession(persistentStorage);
      throw new CloudSessionError('expired', 'Cloud session expired.');
    }

    try {
      const refreshed = await refreshCognitoTokens(
        config,
        await getDiscovery(),
        tokens.refreshToken,
        fetcher,
        now,
      );
      saveCloudTokens(refreshed, persistentStorage);
      return refreshed.accessToken;
    } catch (error) {
      if (error instanceof TypeError) throw error;
      clearSession(persistentStorage);
      throw new CloudSessionError('expired', 'Cloud session expired.');
    }
  }

  const api = createCloudApiClient(config.apiBaseUrl, getAccessToken, fetcher);

  async function beginLogin(returnTo = '/cloud'): Promise<string> {
    return createAuthorizationUrl(
      config,
      await getDiscovery(),
      returnTo,
      transactionStorage,
    );
  }

  async function restore(
    currentUrl: string = window.location.href,
  ): Promise<CloudSessionSnapshot> {
    const url = new URL(currentUrl);
    if (url.pathname.endsWith('/cloud/callback')) {
      const result = await exchangeAuthorizationCallback(
        config,
        await getDiscovery(),
        currentUrl,
        transactionStorage,
        fetcher,
        now,
      );
      saveCloudTokens(result.tokens, persistentStorage);
    }

    if (loadCloudTokens(persistentStorage) === null) {
      return { status: 'signed-out' };
    }

    try {
      const principal = await api.getMe();
      savePrincipal(principal, persistentStorage);
      return { isOffline: false, principal, status: 'authenticated' };
    } catch (error) {
      if (
        error instanceof CloudSessionError ||
        (error instanceof CloudApiError && error.kind === 'unauthorized')
      ) {
        clearSession(persistentStorage);
        return { status: 'signed-out' };
      }

      const principal = loadPrincipal(persistentStorage);
      if (
        principal !== null &&
        (error instanceof TypeError ||
          (error instanceof CloudApiError && error.kind === 'network'))
      ) {
        return { isOffline: true, principal, status: 'authenticated' };
      }
      throw error;
    }
  }

  async function completeLogout(): Promise<string | null> {
    clearSession(persistentStorage);
    try {
      return createLogoutUrl(config, await getDiscovery());
    } catch {
      return null;
    }
  }

  return { api, beginLogin, completeLogout, getAccessToken, restore };
}

function savePrincipal(principal: CloudPrincipal, storage: StorageLike): void {
  storage.setItem(PRINCIPAL_STORAGE_KEY, JSON.stringify(principal));
}

function loadPrincipal(storage: StorageLike): CloudPrincipal | null {
  const raw = storage.getItem(PRINCIPAL_STORAGE_KEY);
  if (raw === null) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (!isRecord(value)) return null;
    const email = value.email;
    const subject = value.subject;
    if (typeof subject !== 'string') return null;
    let parsedEmail: string | null;
    if (email === null) {
      parsedEmail = null;
    } else if (typeof email === 'string') {
      parsedEmail = email;
    } else {
      return null;
    }
    return { email: parsedEmail, subject };
  } catch {
    return null;
  }
}

function clearSession(storage: StorageLike): void {
  clearCloudTokens(storage);
  storage.removeItem(PRINCIPAL_STORAGE_KEY);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
